import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Check, Link2, Unlink, RefreshCw, AlertCircle, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useLanguage } from '../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Localised strings for the WordPress integration card.
const STRINGS = {
  de: {
    loading: 'WordPress-Status wird geladen…',
    connectedAs: 'Verbunden als',
    notConnected: 'Nicht verbunden',
    disconnect: 'Trennen',
    fillAllFields: 'Bitte alle drei Felder ausfüllen.',
    connectedToast: 'WordPress verbunden als',
    connectionFailed: 'Verbindung fehlgeschlagen.',
    confirmDisconnect: 'WordPress-Verbindung wirklich trennen? Fehlgeschlagene oder geplante Posts sind davon nicht betroffen.',
    disconnectedToast: 'WordPress-Verbindung getrennt.',
    disconnectFailed: 'Trennen fehlgeschlagen.',
    defaultCategorySaved: 'Standard-Kategorie gespeichert.',
    saveFailed: 'Speichern fehlgeschlagen.',
    catsLoaded: 'Kategorien geladen.',
    catsFailed: 'Kategorien konnten nicht geladen werden.',
    intro1: 'Verbinde deine WordPress-Seite, um generierte Blog-Artikel direkt zu veröffentlichen. ',
    intro2a: 'Anwendungs-Passwort in WordPress erstellen unter ',
    intro2emphasis: 'Benutzer → Profil → Anwendungspasswörter',
    intro3: '. Erfordert WordPress 5.6+, HTTPS und nicht deaktivierte REST-API.',
    siteUrl: 'Website-URL',
    username: 'Benutzername',
    appPassword: 'Anwendungs-Passwort',
    connect: 'Verbinden',
    checking: 'Verbindung wird geprüft…',
    defaultCategory: 'Standard-Kategorie',
    none: '— keine —',
    refresh: 'Aktualisieren',
    noCats: 'Noch keine Kategorien geladen — klicke ',
    noCatsBtn: 'Aktualisieren',
  },
  en: {
    loading: 'Loading WordPress status…',
    connectedAs: 'Connected as',
    notConnected: 'Not connected',
    disconnect: 'Disconnect',
    fillAllFields: 'Please fill in all three fields.',
    connectedToast: 'WordPress connected as',
    connectionFailed: 'Connection failed.',
    confirmDisconnect: 'Really disconnect WordPress? Failed or scheduled posts are unaffected.',
    disconnectedToast: 'WordPress disconnected.',
    disconnectFailed: 'Disconnect failed.',
    defaultCategorySaved: 'Default category saved.',
    saveFailed: 'Save failed.',
    catsLoaded: 'categories loaded.',
    catsFailed: 'Could not load categories.',
    intro1: 'Connect your WordPress site to publish generated blog articles directly. ',
    intro2a: 'Create an Application Password in WordPress under ',
    intro2emphasis: 'Users → Profile → Application Passwords',
    intro3: '. Requires WordPress 5.6+, HTTPS and REST API enabled.',
    siteUrl: 'Site URL',
    username: 'Username',
    appPassword: 'Application password',
    connect: 'Connect',
    checking: 'Testing connection…',
    defaultCategory: 'Default category',
    none: '— none —',
    refresh: 'Refresh',
    noCats: 'No categories loaded yet — click ',
    noCatsBtn: 'Refresh',
  },
};

/**
 * WordPressCard — connect/disconnect a project's WordPress site and pick a
 * default category. Lives inside Settings.js integrations tab.
 */
export default function WordPressCard({ getAuthHeader, embedded = false, onStatusChange }) {
  const { lang } = useLanguage();
  const t = STRINGS[lang === 'en' ? 'en' : 'de'];
  const [status, setStatus] = useState({ loading: true, connected: false, connection: null });
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [defaultCategoryId, setDefaultCategoryId] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [refreshingCats, setRefreshingCats] = useState(false);

  // onStatusChange via ref so `load` never depends on an unstable callback prop
  // (would otherwise loop: load → onStatusChange → parent re-render → new
  // callback → load changes → effect refires → …). See SupabaseCard.
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  // Last successfully loaded connected value (null = never). On a failed reload
  // we keep this instead of downgrading a known-good status to "false".
  const lastConnectedRef = useRef(null);

  const load = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/integrations/wordpress/status`, { headers: getAuthHeader(), timeout: 8000 });
      const connected = !!data.connected;
      lastConnectedRef.current = connected;
      setStatus({ loading: false, connected, connection: data.connection || null });
      if (data.connection?.default_category_id) {
        setDefaultCategoryId(String(data.connection.default_category_id));
      }
      onStatusChangeRef.current?.(connected);
    } catch (_e) {
      // Error/timeout: never overwrite a known-good status. Keep the last loaded
      // state (only stop the spinner) and report the preserved value.
      setStatus((s) => ({ ...s, loading: false }));
      onStatusChangeRef.current?.(lastConnectedRef.current ?? false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setConnectError('');
    if (!siteUrl.trim() || !username.trim() || !appPassword.trim()) {
      setConnectError('Bitte alle drei Felder ausfüllen.');
      return;
    }
    setConnecting(true);
    try {
      const { data } = await axios.post(
        `${API}/integrations/wordpress/connect`,
        { site_url: siteUrl.trim(), username: username.trim(), app_password: appPassword.trim() },
        { headers: getAuthHeader(), timeout: 45000 }
      );
      toast.success(`WordPress verbunden als ${data.connection?.wp_user_name || 'user'}.`);
      setSiteUrl(''); setUsername(''); setAppPassword('');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Verbindung fehlgeschlagen.';
      setConnectError(msg);
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('WordPress-Verbindung wirklich trennen? Fehlgeschlagene oder geplante Posts sind davon nicht betroffen.')) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/integrations/wordpress/disconnect`, {}, { headers: getAuthHeader() });
      toast.success('WordPress-Verbindung getrennt.');
      setDefaultCategoryId('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Trennen fehlgeschlagen.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveDefaultCategory = async (value) => {
    setSavingCategory(true);
    try {
      const catId = value === '' ? null : Number(value);
      await axios.put(
        `${API}/integrations/wordpress/default-category`,
        { category_id: catId },
        { headers: getAuthHeader() }
      );
      setDefaultCategoryId(value);
      toast.success('Standard-Kategorie gespeichert.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleRefreshCategories = async () => {
    setRefreshingCats(true);
    try {
      const { data } = await axios.get(
        `${API}/integrations/wordpress/categories?refresh=true`,
        { headers: getAuthHeader(), timeout: 30000 }
      );
      setStatus((s) => ({
        ...s,
        connection: s.connection ? { ...s.connection, categories_cache: data.categories || [] } : s.connection,
      }));
      toast.success(`${(data.categories || []).length} Kategorien geladen.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kategorien konnten nicht geladen werden.');
    } finally {
      setRefreshingCats(false);
    }
  };

  if (status.loading) {
    return (
      <div className="bg-[#1A2B42] rounded-lg p-5 flex items-center gap-3" data-testid="wordpress-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#00D9C8]" />
        <span className="text-sm text-[#A0AEC0]">WordPress-Status wird geladen…</span>
      </div>
    );
  }

  const c = status.connection;
  const cats = c?.categories_cache || [];

  return (
    <div className={embedded ? '' : 'bg-[#1A2B42] rounded-lg p-5'} data-testid="wordpress-integration-card">
      <div className={`flex items-center justify-between ${embedded ? 'mb-3' : 'mb-4'}`}>
        <div className="flex items-center gap-3">
          {!embedded && (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#21759B]">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor" aria-hidden="true">
                <path d="M12.158 12.786 9.46 20.625a9 9 0 0 0 5.526-.144.751.751 0 0 1-.062-.116l-2.766-7.579zM3.009 12A9 9 0 0 0 8.077 20.11l-4.29-11.75A8.98 8.98 0 0 0 3.009 12M20.5 8.577A6.98 6.98 0 0 0 20.999 12a9 9 0 0 1-1.199 4.499l-3.85-11.174c1.29 1.043 2.284 2.353 2.906 3.855.264.643.474 1.325.644 2.033zM12 3a9 9 0 0 1 8.093 5.078H16.02a3.65 3.65 0 0 0-3.65-3.65h-.37a3.65 3.65 0 0 0-3.65 3.65h-.048a5.61 5.61 0 0 0-.72.056 8.98 8.98 0 0 1 4.418-5.134M12 21c-.478 0-.947-.038-1.405-.11l3.9-11.325 3.99 10.94A8.98 8.98 0 0 1 12 21"/>
              </svg>
            </div>
          )}
          <div>
            {!embedded && <h3 className="text-white font-medium">WordPress</h3>}
            {status.connected ? (
              <p className="text-xs flex items-center gap-1 text-emerald-400" data-testid="wordpress-connection-status">
                <Check className="w-3 h-3" /> {t.connectedAs} <span className="font-medium">{c?.wp_user_name}</span>
              </p>
            ) : (
              <p className="text-xs text-[#5A6C7D]" data-testid="wordpress-connection-status">{t.notConnected}</p>
            )}
          </div>
        </div>
        {status.connected && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            data-testid="wordpress-disconnect-btn"
          >
            {disconnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />}
            {t.disconnect}
          </Button>
        )}
      </div>

      {!status.connected && (
        <div className="space-y-3">
          <p className="text-xs text-[#A0AEC0] leading-relaxed">
            {t.intro1}{t.intro2a}<em>{t.intro2emphasis}</em>{t.intro3}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.siteUrl}</Label>
              <Input
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://meineseite.de"
                className="bg-[#0A1628] border-transparent text-white text-sm mt-1"
                data-testid="wordpress-site-url-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.username}</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="bg-[#0A1628] border-transparent text-white text-sm mt-1"
                data-testid="wordpress-username-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-[#A0AEC0]">{t.appPassword}</Label>
              <Input
                type="password"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="abcd EFGH ijkl MNOP qrst UVWX"
                className="bg-[#0A1628] border-transparent text-white text-sm mt-1"
                data-testid="wordpress-app-password-input"
              />
            </div>
          </div>

          {connectError && (
            <div className="flex items-start gap-2 text-xs bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded px-3 py-2" data-testid="wordpress-connect-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{connectError}</span>
            </div>
          )}

          <Button
            onClick={handleConnect}
            disabled={connecting}
            className="bg-[#21759B] hover:bg-[#1a5f7f] text-white font-medium"
            data-testid="wordpress-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
            {connecting ? t.checking : t.connect}
          </Button>
        </div>
      )}

      {status.connected && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
            <ExternalLink className="w-3.5 h-3.5" />
            <a href={c?.site_url} target="_blank" rel="noreferrer" className="text-[#00D9C8] hover:underline break-all">
              {c?.site_url}
            </a>
            {c?.wp_user_roles?.length > 0 && (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 ml-1">
                {c.wp_user_roles.join(', ')}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.defaultCategory}</Label>
              <select
                value={defaultCategoryId}
                onChange={(e) => handleSaveDefaultCategory(e.target.value)}
                disabled={savingCategory || cats.length === 0}
                className="w-full mt-1 bg-[#0A1628] border border-transparent text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-[#00D9C8]"
                data-testid="wordpress-default-category-select"
              >
                <option value="">{t.none}</option>
                {cats.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshCategories}
              disabled={refreshingCats}
              className="border-[#00D9C8]/40 text-[#00D9C8] hover:bg-[#00D9C8]/10 h-9"
              data-testid="wordpress-refresh-categories-btn"
            >
              {refreshingCats ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {t.refresh}
            </Button>
          </div>
          {cats.length === 0 && (
            <p className="text-xs text-amber-300/80">{t.noCats}&bdquo;{t.noCatsBtn}&ldquo;.</p>
          )}
        </div>
      )}
    </div>
  );
}
